import React, { useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import apiService from '@/api-service';

interface ExportColumn {
    key: string;
    label: string;
    category: string;
}

interface OvkExportParams {
    filter_text?: string;
}

interface Props {
    show: boolean;
    onHide: () => void;
    filterParams: OvkExportParams;
    totalCount: number;
}

const CSV_EXPORT_COLUMNS: ExportColumn[] = [
    // Basis OVK info
    { key: 'ovk_nummer', label: 'OVK Nummer', category: 'Overeenkomsthouder' },
    { key: 'ovk_naam', label: 'OVK Naam', category: 'Overeenkomsthouder' },
    { key: 'ovk_locatie', label: 'Locatie', category: 'Overeenkomsthouder' },
    
    // Statistieken
    { key: 'bestekken_count', label: 'Aantal Bestekken', category: 'Statistieken' },
    { key: 'bestekken_nummers', label: 'Bestek Nummers', category: 'Statistieken' },
    { key: 'bestekken_beschrijvingen', label: 'Bestek Beschrijvingen', category: 'Statistieken' },
    
    // Meta informatie (als beschikbaar)
    { key: 'created_at', label: 'Aangemaakt op', category: 'Meta' },
    { key: 'updated_at', label: 'Bijgewerkt op', category: 'Meta' },
];

export default function OvkCSVExportModal({ show, onHide, filterParams, totalCount }: Props) {
    const [selectedColumns, setSelectedColumns] = useState<ExportColumn[]>([
        CSV_EXPORT_COLUMNS.find(c => c.key === 'ovk_nummer')!,
        CSV_EXPORT_COLUMNS.find(c => c.key === 'ovk_naam')!,
        CSV_EXPORT_COLUMNS.find(c => c.key === 'ovk_locatie')!,
        CSV_EXPORT_COLUMNS.find(c => c.key === 'bestekken_count')!,
    ]);
    const [isExporting, setIsExporting] = useState(false);
    const [exportFormat, setExportFormat] = useState<'csv' | 'xlsx'>('csv');
    const [maxRows, setMaxRows] = useState<number>(Math.min(1000, totalCount));

    // Groepeer kolommen per categorie voor betere UX
    const groupedColumns = CSV_EXPORT_COLUMNS.reduce((groups, column) => {
        if (!groups[column.category]) {
            groups[column.category] = [];
        }
        groups[column.category].push(column);
        return groups;
    }, {} as { [category: string]: ExportColumn[] });

    const handleExport = async () => {
        if (selectedColumns.length === 0) {
            alert('Selecteer minimaal één kolom om te exporteren.');
            return;
        }

        setIsExporting(true);
        
        try {
            const exportParams = {
                search_params: {
                    ...filterParams,
                    max_rows: maxRows
                },
                format: exportFormat,
                export_columns: selectedColumns.map(col => col.key),
                max_rows: maxRows
            };

            const response = await apiService.post('/ovk/export', exportParams, {
                responseType: 'blob'
            });

            // Create download link
            const blob = new Blob([response.data], {
                type: exportFormat === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `overeenkomsthouders_export_${new Date().toISOString().split('T')[0]}.${exportFormat}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            onHide();
        } catch (error) {
            console.error('Export failed:', error);
            alert('Export mislukt. Probeer het opnieuw.');
        } finally {
            setIsExporting(false);
        }
    };

    const selectAllInCategory = (category: string) => {
        const categoryColumns = groupedColumns[category];
        const newSelected = [...selectedColumns];
        
        categoryColumns.forEach(col => {
            if (!newSelected.find(selected => selected.key === col.key)) {
                newSelected.push(col);
            }
        });
        
        setSelectedColumns(newSelected);
    };

    const deselectAllInCategory = (category: string) => {
        const categoryColumns = groupedColumns[category];
        const newSelected = selectedColumns.filter(col => 
            !categoryColumns.find(catCol => catCol.key === col.key)
        );
        setSelectedColumns(newSelected);
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Overeenkomsthouders Exporteren naar {exportFormat.toUpperCase()}</Modal.Title>
            </Modal.Header>
            
            <Modal.Body>
                <div className="mb-3">
                    <strong>Export instellingen:</strong>
                    <div className="mt-2">
                        <Form.Check
                            type="radio"
                            id="format-csv"
                            label="CSV formaat"
                            checked={exportFormat === 'csv'}
                            onChange={() => setExportFormat('csv')}
                        />
                        <Form.Check
                            type="radio"
                            id="format-excel"
                            label="Excel formaat"
                            checked={exportFormat === 'xlsx'}
                            onChange={() => setExportFormat('xlsx')}
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <Form.Label>Maximum aantal rijen (max {totalCount}):</Form.Label>
                    <Form.Control
                        type="number"
                        value={maxRows}
                        onChange={(e) => setMaxRows(Math.min(parseInt(e.target.value) || 1000, totalCount))}
                        min={1}
                        max={totalCount}
                    />
                </div>

                <div className="mb-3">
                    <Form.Label>Geselecteerde kolommen ({selectedColumns.length}):</Form.Label>
                    <Select
                        isMulti
                        value={selectedColumns}
                        onChange={(newValue) => setSelectedColumns([...newValue])}
                        options={CSV_EXPORT_COLUMNS}
                        getOptionLabel={(option) => `${option.label} (${option.category})`}
                        getOptionValue={(option) => option.key}
                        placeholder="Selecteer kolommen..."
                        className="mb-2"
                    />
                </div>

                <div>
                    <strong>Kolommen per categorie:</strong>
                    {Object.entries(groupedColumns).map(([category, columns]) => (
                        <div key={category} className="mt-2 p-2 border rounded">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <strong>{category}</strong>
                                <div>
                                    <Button
                                        size="sm"
                                        variant="outline-primary"
                                        className="me-1"
                                        onClick={() => selectAllInCategory(category)}
                                    >
                                        Alles selecteren
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={() => deselectAllInCategory(category)}
                                    >
                                        Alles deselecteren
                                    </Button>
                                </div>
                            </div>
                            
                            <div className="row">
                                {columns.map(column => (
                                    <div key={column.key} className="col-md-6">
                                        <Form.Check
                                            type="checkbox"
                                            id={`col-${column.key}`}
                                            label={column.label}
                                            checked={selectedColumns.some(sel => sel.key === column.key)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedColumns([...selectedColumns, column]);
                                                } else {
                                                    setSelectedColumns(selectedColumns.filter(sel => sel.key !== column.key));
                                                }
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </Modal.Body>
            
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={isExporting}>
                    Annuleren
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleExport}
                    disabled={isExporting || selectedColumns.length === 0}
                >
                    {isExporting ? (
                        <>
                            <Spinner size="sm" className="me-1" />
                            Exporteren...
                        </>
                    ) : (
                        `Exporteer ${selectedColumns.length} kolommen`
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}